import { Injectable } from '@angular/core';
import { isNullOrUndefined } from '../../../util/is-null-or-undefined.util';

import { jwtDecode } from 'jwt-decode';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RegrasAcessosModulos } from '../../autorizacao/models/regras-acessos-modulos.model';
import { AppSettingsService } from '../../configuracao/services/app-settings.service';
import { Disclaimer } from '../../models/notificacoes.model';
import { Operacao } from '../models/operacao.model';
import { DadosUsuario } from '../models/resposta-login-charon.model';
import { SessaoLogin } from '../models/sessao-login.model';
import { Link } from '../models/session.model';
import { CharonService, IInitializeParam, IHttpOptions } from '@quickweb/connect-angular';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '@asset-old/env/environment';

@Injectable({
  providedIn: 'root',
})
export class SessaoService {
  private sessaoLogin: SessaoLogin;
  private readonly submenuDataKey = 'submenuData';
  /**
   * Flag indicating whether the session has been loaded into memory.
   * Once true, subsequent reads use the in-memory cache instead of localStorage.
   */
  private _isSessionLoaded = false;

  constructor(
    private http: HttpClient,
    private servicoConfiguracao: AppSettingsService,
    private charon: CharonService
  ) {
    this.sessaoLogin = new SessaoLogin();
  }

  /**
   * Armazena no storage objeto com dados de autenticação.
   * @param sessao objeto contendo os dados de autenticação
   */
  persistirSessao(sessao: SessaoLogin): void {
    // Invalidate cache before clearing
    this._isSessionLoaded = false;
    localStorage.removeItem('sessaoLogin');
    this.sessaoLogin = new SessaoLogin();
    
    if (!isNullOrUndefined(sessao)) {
      // Decode usuario from JWT if present
      if (!isNullOrUndefined(sessao.tokenJwt)) {
        sessao.usuario = jwtDecode(sessao.tokenJwt) as DadosUsuario;
      }
      // Update in-memory cache directly
      this.sessaoLogin = sessao;
      this._isSessionLoaded = true;
      // Persist to localStorage
      localStorage.setItem('sessaoLogin', JSON.stringify(this.sessaoLogin));
    }
  }

  /**
   * Recupera do storage objeto da operação de acordo com seu apelido.
   * @param apelido apelido da operação que será executada no router
   */
  obterOperacao(apelido: string): Operacao {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.operacoes)
    ) {
      for (let i = 0; i < this.sessaoLogin.operacoes.length; i++) {
        if (this.sessaoLogin.operacoes[i].target === apelido) {
          return this.sessaoLogin.operacoes[i];
        }
      }
    }
  }

  /**
   * Recupera do storage o target (apelido) da operação de acordo com o código da op.
   * @param codOperacao código da operação que será executada no router
   */
  obterApelidoDaOperacao(codOperacao: string): string {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.operacoes)
    ) {
      for (let i = 0; i < this.sessaoLogin.operacoes.length; i++) {
        if (this.sessaoLogin.operacoes[i].op === codOperacao) {
          return this.sessaoLogin.operacoes[i].target;
        }
      }
    }
  }

  /**
   * Recupera do storage uma lista de permissões.
   * @returns Lista de strings representando as permissões
   */
  obterPermissoes(): Array<string> {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.usuario) &&
      !isNullOrUndefined(this.sessaoLogin.usuario.permissoes)
    ) {
      let permissoes = this.sessaoLogin.usuario.permissoes;

      return permissoes;
    } else {
      return new Array<string>();
    }
  }

  /**
   * Recupera do storage uma lista de toggles.
   * @returns Lista de strings representando os toggles
   */
  obterToggles(): Array<string> {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.usuario) &&
      !isNullOrUndefined(this.sessaoLogin.usuario.feature_flags)
    ) {
      let toggles = this.sessaoLogin.usuario.feature_flags;
      return toggles;
    } else {
      return new Array<string>();
    }
  }



  /**
   * Recupera session id (Session-Id) retornado pelo logon, se presente.
   */
  obterSessionId(): string {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined((this.sessaoLogin as any).sessionId)
    ) {
      return (this.sessaoLogin as any).sessionId;
    }
    return '';
  }

  obterTokenJwt(): string {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.tokenJwt)
    ) {
      return this.sessaoLogin.tokenJwt;
    } else {
      return '';
    }
  }

  /**
   * Recupera ebusiness id do storage.
   */
  obterEbusiness(): string {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.usuario) &&
      !isNullOrUndefined(this.sessaoLogin.usuario.id_usuario)
    ) {
      return this.sessaoLogin.usuario.id_usuario;
    } else {
      return '';
    }
  }

  /**
   * Recupera o cnpj do usuário
   */
  obterCnpj(apenasNumeros: boolean): string {
    this.obterSessao();
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.usuario.cnpj)
    ) {
      if (apenasNumeros) {
        return this.sessaoLogin.usuario.cnpj
          .replace('.', '')
          .replace('.', '')
          .replace('/', '')
          .replace('-', '');
      }

      return this.sessaoLogin.usuario.cnpj;
    } else {
      return '';
    }
  }

  /**
   * Remove objeto com dados de autenticação armazenado no storage.
   * Also invalidates the in-memory cache.
   */
  excluirSessao(): void {
    // local-only cleanup: synchronous and deterministic
    localStorage.removeItem('sessaoLogin');
    this.sessaoLogin = new SessaoLogin();
    this._isSessionLoaded = false;
  }

  /**
   * Notify Caronte logout endpoint using the same headers used during authentication.
   * This sends no body and ignores any errors (best-effort notification).
   */
  // Backwards-compatible network notifier (returns Observable)
  notificarLogout(userAgent?: string): Observable<any> {
    try {
      this.charon.setCryptography(environment.charon.isCryptographed);

      const token = this.obterTokenJwt();
      const sessionId = this.obterSessionId();

      const headersObj: { [key: string]: string } = {
        'Content-Type': 'application/json',
        accept: '*/*',
        'x-itau-correlationID': uuidv4(),
      } as any;

      if (token) {
        headersObj['Authorization'] = `Bearer ${token}`;
      }
      if (sessionId) {
        headersObj['Session-Id'] = sessionId;
      }

      const httpOptions: IHttpOptions = {
        headers: new HttpHeaders(headersObj),
        charonID: environment.charon.isCryptographed
          ? this.servicoConfiguracao.environment.charon.id
          : this.servicoConfiguracao.environment.charon.idNoCrypto,
        entryPoint: environment.charon.isCryptographed
          ? this.servicoConfiguracao.environment.charon.entrypoint
          : this.servicoConfiguracao.environment.charon.entrypointNoCrypto,
      };

      const actionLogout =
        this.servicoConfiguracao.environment.charon.action_logout || 'logout';

      // send empty object body to match expected API
      return this.charon.post(actionLogout, JSON.stringify({}), httpOptions);
    } catch (error) {
      // convert to Observable that completes
      return new Observable((observer) => {
        observer.next(null);
        observer.complete();
      });
    }
  }

  

  /**
   * Recupera do storage o nome do usuário logado
   */
  obterNomeUsuario(): string {
    this.obterSessao();

    const nome = this.sessaoLogin.usuario.usuario;
    return nome
  }

  isJWTExpired(): boolean {
    this.obterSessao();

    const expirationDate = new Date(this.sessaoLogin.usuario.exp * 1000);

    return new Date() > expirationDate;
  }

  /**
   * Verifica se token do caronte expirou
   */
  isCharonExpired(): boolean {
    this.obterSessao();

    if (!this.sessaoLogin) return true;

    const issuedDate = new Date(this.sessaoLogin.charonTokenIssuedDate);
    const currentDate = new Date();

    function diff_minutes(dt2, dt1) {
      var diff = (dt2.getTime() - dt1.getTime()) / 1000;
      diff /= 60;
      return Math.abs(Math.round(diff));
    }
    return (
      diff_minutes(issuedDate, currentDate) >
      this.servicoConfiguracao.environment.charon.timeout
    );
  }

  /**
   * Verifica se o usuário possui permissão de acesso a determiando módulo
   * @param modulo Módulo que será verificado a permissão do usuário
   * @returns true se possui permissão e false se não possui permissão
   */
  usuarioPossuiPermissao(modulo: RegrasAcessosModulos): boolean {
    const permissoes = this.obterPermissoes();
    const moduloProcurado = modulo.toString();

    return permissoes.some((p) => p == moduloProcurado);
  }

  /**
   * Recupera do storage uma lista de links ofuscados pelo Caronte.
   * @returns Lista de strings representando os links
   */
  obterLinksCharon(): Link {
    const response = sessionStorage.getItem(environment.charon.isCryptographed ? this.servicoConfiguracao.environment.charon.id : this.servicoConfiguracao.environment.charon.idNoCrypto);
    if (isNullOrUndefined(response)) return null;

    let session: any;
    try {
      session = JSON.parse(response);
      // If session is still a string, parse again
      if (typeof session === 'string') {
        session = JSON.parse(session);
      }
    } catch (error) {
      console.error('Failed to parse session:', error);
      return null;
    }
    this.sessaoLogin.links = session.links;

    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.links)
    ) {
      return this.sessaoLogin.links;
    } else {
      return null;
    }
  }

  /**
   * Recupera do storage o objeto com dados de autenticação do usuário.
   * Uses in-memory cache if already loaded to avoid repeated localStorage reads and JWT decoding.
   */
  private obterSessao(): void {
    // If already loaded into memory, skip localStorage read
    if (this._isSessionLoaded && !isNullOrUndefined(this.sessaoLogin)) {
      return;
    }

    const stored = localStorage.getItem('sessaoLogin');
    if (isNullOrUndefined(stored)) {
      this.sessaoLogin = new SessaoLogin();
      this._isSessionLoaded = true;
      return;
    }

    this.sessaoLogin = JSON.parse(stored) as SessaoLogin;
    if (
      !isNullOrUndefined(this.sessaoLogin) &&
      !isNullOrUndefined(this.sessaoLogin.tokenJwt)
    ) {
      this.sessaoLogin.usuario = jwtDecode(
        this.sessaoLogin.tokenJwt,
      ) as DadosUsuario;
    }
    this._isSessionLoaded = true;
  }

  /**
   * Invalidates the in-memory session cache, forcing the next read to load from localStorage.
   * Use this when external changes to localStorage are expected.
   */
  invalidarCache(): void {
    this._isSessionLoaded = false;
  }

  getCharonSession(): Observable<any> {
    this.charon.setCryptography(environment.charon.isCryptographed);
    const params: IInitializeParam = {
      entryPoint: environment.charon.isCryptographed ? this.servicoConfiguracao.environment.charon.entrypoint : this.servicoConfiguracao.environment.charon.entrypointNoCrypto,
      charonID: environment.charon.isCryptographed ? this.servicoConfiguracao.environment.charon.id : this.servicoConfiguracao.environment.charon.idNoCrypto,
    };
    return this.charon.initialize(params);
  }

  /**
   * Inicializa sessão Caronte com opções customizadas (ex: desligar criptografia
   * temporariamente ou forçar outro ID). Ao omitir options mantém comportamento padrão.
   */
  getCharonSessionWithOptions(options?: {
    cryptography?: boolean;
    charonID?: string;
  }): Observable<any> {
    const useCrypto = isNullOrUndefined(options?.cryptography)
      ? environment.charon.isCryptographed
      : options.cryptography;
    this.charon.setCryptography(useCrypto);
    const params: IInitializeParam = {
      entryPoint: useCrypto ? environment.charon.entrypoint : environment.charon.entrypointNoCrypto,
      charonID: options?.charonID || ( environment.charon.isCryptographed? this.servicoConfiguracao.environment.charon.id: this.servicoConfiguracao.environment.charon.idNoCrypto),
    };
    return this.charon.initialize(params);
  }

  resetarSessaoCaronte(
    links: Link,
    issuedDate: Date,
  ): void {
    // Obtain current session and update only Caronte-specific fields.
    // Do not clear the whole session (excluirSessao) because that removes
    // `usuario` and `tokenJwt` and causes the UI to lose authentication state.
    this.obterSessao();
    if (isNullOrUndefined(this.sessaoLogin)) {
      this.sessaoLogin = new SessaoLogin();
    }

    // Preserve existing `tokenJwt`, `usuario` and other auth fields while
    // updating the Caronte links and issued date used for session renewal.
    this.sessaoLogin.links = links;
    this.sessaoLogin.charonTokenIssuedDate = issuedDate;

    // Update localStorage (in-memory cache is already up-to-date)
    localStorage.setItem('sessaoLogin', JSON.stringify(this.sessaoLogin));
  }

  refreshJWT(newToken: string) {
    if (newToken) {
      this.obterSessao();
      this.sessaoLogin.tokenJwt = newToken;
      // Decode the new token to update usuario
      this.sessaoLogin.usuario = jwtDecode(newToken) as DadosUsuario;
      // Persist directly to avoid clearing session
      localStorage.setItem('sessaoLogin', JSON.stringify(this.sessaoLogin));
    }
  }

  hasSpecialPermissions(): boolean {
    //Checa se FEATURE_OPERADORES_ESPECIAIS está no array de toggles
    this.obterSessao();
    if (isNullOrUndefined(this.sessaoLogin.usuario.feature_flags)) return false;
    return this.sessaoLogin.usuario.feature_flags.includes(
      'FEATURE_OPERADORES_ESPECIAIS',
    );
  }

  adjustDateToSaoPauloTimeZone(date: Date): Date {
    // São Paulo time zone offset in hours, typically -3 but could vary
    const saoPauloOffsetHours = -3;
    // Convert hours to milliseconds
    const saoPauloOffsetMs = saoPauloOffsetHours * 60 * 60 * 1000;
    // Get the current UTC offset for the date in milliseconds
    const currentOffsetMs = date.getTimezoneOffset() * 60 * 1000;
    // Adjust the date to São Paulo time
    return new Date(date.getTime() - currentOffsetMs);
  }

  obterDisclaimer(): Disclaimer[] | null {
    this.obterSessao();
    if (
      isNullOrUndefined(this.sessaoLogin) ||
      isNullOrUndefined(this.sessaoLogin.usuario) ||
      isNullOrUndefined(this.sessaoLogin.usuario.feature_configs)
    ) {
      return null;
    }

    const featureConfigs = this.sessaoLogin.usuario.feature_configs;
    const disclaimerConfig = Array.isArray(featureConfigs)
      ? featureConfigs.find(
          (config) => config.nome === 'FEATURE_DISCLAIMER_MENSAGEM',
        )?.valor
      : featureConfigs?.valor;

    if (!disclaimerConfig) {
      return null;
    }

    try {
      const disclaimers: Disclaimer[] = JSON.parse(disclaimerConfig);
      const dataAtual = new Date();
      return disclaimers.filter(({ data_inicio, data_fim }) => {
        const dataInicio = new Date(data_inicio);
        const dataFim = new Date(data_fim);
        return dataInicio <= dataAtual && dataAtual <= dataFim;
      });
    } catch (error) {
      console.error('Failed to parse disclaimer config:', error);
      return null;
    }
  }

  hasPACPermissions(): boolean {
    this.obterSessao();
    if (isNullOrUndefined(this.sessaoLogin.usuario.feature_flags)) return false;
    //Checa se FEATURE_MODAL_PAC está no array de toggles
    return this.sessaoLogin.usuario.feature_flags.includes(
      'FEATURE_TELA_CARTEIRA',
    );
  }

  hasUploadTaxesPermissions(): boolean {
    this.obterSessao();
    if (isNullOrUndefined(this.sessaoLogin.usuario.feature_flags)) return false;
    //Checa de FEATURE_MODAL_TAXAS está no array de toggles
    return this.sessaoLogin.usuario.feature_flags.includes(
      'FEATURE_MODAL_TAXAS',
    );
  }

  /**
   * Checks if the user is authenticated.
   * @returns {boolean} True if authenticated, false otherwise.
   */
  estaAutenticado(): boolean {
    this.obterSessao();
    if (this.sessaoLogin && this.sessaoLogin.tokenJwt && !this.isJWTExpired()) {
      return true;
    }
    return false;
  }

  setSubmenuData(data: any): void {
    sessionStorage.setItem(this.submenuDataKey, JSON.stringify(data));
  }

  getSubmenuData(): any {
    const data = sessionStorage.getItem(this.submenuDataKey);
    return data ? JSON.parse(data) : null;
  }
}
